import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, useTheme } from '@material-ui/core'
import { TablePaginationActionsProps } from '@material-ui/core/TablePagination/TablePaginationActions'
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight'
import { makeStyles } from '@material-ui/styles'
import { HeaderBlock } from 'components/header-block/HeaderBlock'
import { Loading } from 'components/loading/Loading'
import PageContainer from 'components/page-container/PageContainer'
import TableV2 from 'components/table/TableV2'
import { DeleteModal } from 'containers/delete-modal/DeleteModal'
import { MUIDataTableColumn } from 'mui-datatables'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { clearContinuationToken, createCodedValue, deleteCodedValues, getCodedValues, getCodeTypes, updateCodedValue } from 'state/actions/CodedValueActions'
import { RootState } from 'state/reducers/AppReducer'
import { CodedValue } from 'state/reducers/CodedValueReducer'
import { FixMeLater } from 'types/FixMeLaterType'

const useStyles = makeStyles((theme: FixMeLater) => ({
    grid: {
        height: "100%"
    },
    gridItem: {
        height: "80%"
    },
    tablePaginationRoot: {
        flexShrink: 0,
        marginLeft: theme.spacing(2.5),
    },
}))

function ListCodedValues() {

    const codedValues = useSelector<RootState, { [index: string]: CodedValue }>(state => state.CodedValueReducer.codedValues)
    const codedValue = useSelector<RootState, CodedValue | null>(state => state.CodedValueReducer.currentCodedValue)
    const continuationToken = useSelector<RootState, string | null>(state => state.CodedValueReducer.continuationToken)
    const codeTypes = useSelector<RootState, string[]>(state => state.CodedValueReducer.codeTypes)

    const classes = useStyles()
    const dispatch = useDispatch()
    const [t, i18n] = useTranslation()
    const theme = useTheme();

    const [codedValueViewOpen, setCodedValueViewOpen] = useState(false)
    const [codedValueDeleteOpen, setCodedValueDeleteOpen] = useState(false)

    const [currentCodedValue, setCurrentCodedValue] = useState<CodedValue | null>(null)
    const [codedValuesToDelete, setCodedValuesToDelete] = useState<string[] | null>(null)

    const [rowSize, setRowSize] = useState(25)
    const [currentPage, setCurrentPage] = useState(0)
    const [pageMap, setPageMap] = useState<{ [index: string]: string | null }>({ 0: null })
    const [filter, setFilter] = useState<FixMeLater | null>(null)

    const [loading, setLoading] = useState(false)

    const [fetchCodedValues, setFetchCodedValues] = useState(true)

    useEffect(() => {
        dispatch(getCodeTypes(() => { }))
    }, [codedValues])

    useEffect(() => {
        updatePageMap(currentPage, continuationToken)
    }, [continuationToken])

    useEffect(() => {
        if (fetchCodedValues) {
            setFetchCodedValues(false)
            setLoading(true)
            dispatch(getCodedValues((success: boolean) => {
                setLoading(false)
            }, rowSize, filter, null, pageMap[currentPage]))
        }
    }, [fetchCodedValues])

    const updatePageMap = (pageNumber: number, token: string | null) => {
        const currentPageMap = Object.assign({}, pageMap)

        if (token != null) {
            currentPageMap[pageNumber + 1] = token
        }

        setPageMap(currentPageMap)
    }

    const clearTokenAndRefetch = useCallback(() => {
        dispatch(clearContinuationToken(() => {
            updatePageMap(0, null)
            setFetchCodedValues(true)
        }))
    }, [setFetchCodedValues])

    const fetchCodedValueFromId = useCallback((ids: string[] | null) => {
        if (!!ids) {

            return ids.map((id) => {
                const codedValue = codedValues[id]

                if (!!codedValue) {
                    return { id: codedValue.id, Name: codedValue.CodeValue }
                } else {
                    return { id: id, Name: t("common.unknown-deleted-data") }
                }
            })
        }

        return []
    }, [codedValues])

    const handleSaveCodedValues = () => {
        if (!!currentCodedValue) {
            if (!!currentCodedValue.id) {
                const oldCodedValue: CodedValue = codedValues[currentCodedValue.id]
                if (!!oldCodedValue && !!oldCodedValue.id && currentCodedValue != oldCodedValue) {
                    dispatch(updateCodedValue(currentCodedValue))
                }
            } else {
                dispatch(createCodedValue(currentCodedValue))
            }
        }
    }

    const keySwitch = (key: keyof CodedValue) => {
        const headers: { [index: string]: { key: keyof CodedValue, label: string, validation?: { validate: (value?: string | null) => boolean, errorMessage: string } | null } } = {
            CodeType: {
                key: "CodeType",
                label: "CodeType",
            },
            CodeId: {
                key: "CodeId",
                label: "CodeId",
                validation: {
                    validate: (value) => {
                        var endSpace = /\s$/;
                        return !!value ? endSpace.test(value) : false
                    },
                    errorMessage: "CodeId can not end in a space"
                }
            },
            CodeValue: {
                key: "CodeValue",
                label: "CodeValue"
            }
        }

        const handleChange = (key: keyof CodedValue, value: string) => {
            const codedValue = Object.assign({}, currentCodedValue)
            var data = value

            codedValue[key] = data
            setCurrentCodedValue(codedValue)
        }
        const value = !!currentCodedValue ? currentCodedValue[key] : null
        const error = headers[key].validation?.validate(value)

        switch (key) {

            default:
                return (
                    <TextField
                        label={headers[key].label}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        value={value}
                        error={error}
                        helperText={error ? headers[key].validation?.errorMessage : null}
                        onChange={(event) => {
                            handleChange(key, event.target.value)
                        }}
                    />
                )
        }
    }

    const onRowClick = useCallback(
        (item: CodedValue) => {
            const codedValue = item
            setCurrentCodedValue(codedValue)
            setCodedValueViewOpen(true)
        }, [setCurrentCodedValue, setCodedValueViewOpen]
    )

    const onRowsDelete = useCallback(
        (items: string[]) => {
            setCodedValuesToDelete(items)
            setCodedValueDeleteOpen(true)
        }, [setCodedValuesToDelete, setCodedValueDeleteOpen]
    )

    const onAddCodedValue = useCallback(() => {
        var newCodedValue: CodedValue = {
            CodeType: "",
            CodeId: "",
            CodeValue: ""
        }
        setCodedValueViewOpen(true)
        setCurrentCodedValue(newCodedValue)
    }, [setCodedValueViewOpen, setCurrentCodedValue])


    const onDeleteDone = (deleteList: { id: string, Name: string }[]) => {
        dispatch(deleteCodedValues(deleteList.map(item => item.id), () => {
            setCodedValueDeleteOpen(false)
            setCodedValuesToDelete(null)
        }))
    }

    const onDeleteCancel = () => {
        setCodedValueDeleteOpen(false)
        setCodedValuesToDelete(null)
    }

    const onPageChange = useCallback(
        (page: number) => {
            setCurrentPage(page)
            setFetchCodedValues(true)
        },
        [setCurrentPage, setFetchCodedValues]
    )

    const onRowPerPageChange = useCallback(
        (rowSize: number) => {
            setRowSize(rowSize)
            setCurrentPage(0)
            setFetchCodedValues(true)
        },
        [setRowSize, setCurrentPage, setFetchCodedValues]
    )

    const overrideDisplayedRows = useCallback(
        (to: number, from: number, count: number, page: number) => {
            return `Page: ${page}`
        },
        [],
    )

    const overrideFilter = useCallback(
        (changedColumn: string, filterList: string[][], type: 'checkbox' | 'dropdown' | 'multiselect' | 'textField' | 'custom' | 'chip' | 'reset', changedColumnIndex: number, displayData: any) => {
            const codeTypeFilters = filterList[0]

            if (codeTypeFilters != null && codeTypeFilters.length > 0) {
                setFilter(codeTypeFilters[0]);
            } else {
                setFilter(null)
            }
            clearTokenAndRefetch()
        },
        [setFilter, setFetchCodedValues]
    )

    const columns: MUIDataTableColumn[] = useMemo(() => ([
        {
            name: "CodeType", label: "CodeType",
            options: {
                filter: true,
                sort: false,
                filterType: "dropdown",
                filterOptions: {
                    names: codeTypes,
                    fullWidth: true,
                    logic: () => false
                }
            }
        },
        {
            name: "CodeId", label: "CodeId",
            options: {
                filter: false,
                sort: false,
            }
        },
        {
            name: "CodeValue", label: "CodeValue",
            options: {
                filter: false,
                sort: false,
            }
        }
    ]), [codeTypes])

    const dataList = useMemo(() => Object.values(codedValues), [codedValues])

    const TablePaginationActions = useCallback((props: TablePaginationActionsProps) => {

        const { count, page, rowsPerPage, onChangePage } = props;
        const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            onChangePage(event, page - 1);
        };

        const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            onChangePage(event, page + 1);
        };

        return (
            <Box className={classes.tablePaginationRoot}>
                <IconButton
                    onClick={handleBackButtonClick}
                    disabled={page == 0 || loading}
                    aria-label="previous page">
                    {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                </IconButton>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={!continuationToken || loading}
                    aria-label="next page"
                >
                    {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                </IconButton>
            </Box>
        );
    }, [continuationToken, loading])


    const renderContent = () => {
        return (
            <TableV2
                title={""}
                data={dataList}
                columnFields={columns}
                handleOnRowClick={onRowClick}
                handleOnRowsDelete={onRowsDelete}
                handleAdd={onAddCodedValue}
                handlePageChange={onPageChange}
                handleRowPerPageChange={onRowPerPageChange}
                searchDisabled
                overridePage={currentPage}
                overrideRowsPerPage={rowSize}
                overrideDisplayedRows={overrideDisplayedRows}
                hasMorePages={!!continuationToken}
                customActions={TablePaginationActions}
                overrideFilter={overrideFilter}
                loading={loading}
            />
        )
    }

    const renderCodedValueViewModal = () => {
        return (
            <Dialog open={codedValueViewOpen}>
                <DialogTitle>
                    {currentCodedValue?.id || t('coded-value.new-coded-value')}
                </DialogTitle>
                <DialogContent>
                    <Grid container>
                        {
                            currentCodedValue != null ?
                                // Create view for each key on the CodedValue model
                                Object.keys(currentCodedValue).map((key: keyof CodedValue) => {
                                    if (key == "id") {
                                        return null
                                    }
                                    return (
                                        < Grid item xs={12} >
                                            {keySwitch(key)}
                                        </Grid>
                                    )
                                }) :
                                null
                        }
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setCodedValueViewOpen(false)
                            setCurrentCodedValue(null)
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        disabled={!(!!currentCodedValue
                            && !!currentCodedValue?.CodeType
                            && !!currentCodedValue?.CodeId
                            && !!currentCodedValue?.CodeValue)}
                        onClick={() => {
                            if (!!currentCodedValue
                                && !!currentCodedValue?.CodeType
                                && !!currentCodedValue?.CodeId
                                && !!currentCodedValue?.CodeValue
                            ) {
                                // Save update
                                setCodedValueViewOpen(false)
                                handleSaveCodedValues()
                                setCurrentCodedValue(null)
                            } else {
                                // show error
                            }
                        }}
                    >
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog >
        )
    }

    const renderCodedValueDeleteModal = () => {
        return (
            <DeleteModal
                open={codedValueDeleteOpen}
                data={fetchCodedValueFromId(codedValuesToDelete)}
                handleCancel={onDeleteCancel}
                handleDone={onDeleteDone}
            />
        )
    }

    return (
        <Box>
            <HeaderBlock
                title="Coded Values"
            />

            {renderCodedValueViewModal()}
            {renderCodedValueDeleteModal()}

            <PageContainer >
                {renderContent()}
            </PageContainer>
        </Box>
    )
}

export default ListCodedValues

